import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { api } from "../lib/api";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello — I&apos;m the Soft Spine Press assistant. Ask about our titles, downloads, or anything else." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(localStorage.getItem("ssp_chat_sid") || `web_${Math.random().toString(36).slice(2, 12)}`);
  const scrollRef = useRef(null);

  useEffect(() => { localStorage.setItem("ssp_chat_sid", sessionId.current); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble responding. Please email hello@softspinepress.com." }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="chat-toggle"
        className="fixed bottom-20 right-6 z-50 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-[0_12px_30px_-10px_rgba(58,31,71,0.5)] flex items-center justify-center hover:opacity-90 transition-opacity">
        {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <MessageCircle className="h-5 w-5" strokeWidth={1.5} />}
      </button>
      {open && (
        <div className="fixed bottom-36 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[480px] bg-background border border-border rounded-sm shadow-[0_20px_60px_-20px_rgba(58,31,71,0.4)] flex flex-col" data-testid="chat-widget">
          <div className="px-4 py-3 border-b border-border bg-sidebar text-sidebar-fg">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">Soft Spine Press</div>
            <div className="font-display text-lg">Ask the assistant</div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] text-sm rounded-sm px-3 py-2 ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}
                  dangerouslySetInnerHTML={{ __html: m.content.replace(/&apos;/g, "'") }} />
              </div>
            ))}
            {busy && <div className="text-xs text-muted-foreground italic">Thinking…</div>}
          </div>
          <div className="border-t border-border p-3 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a question…" data-testid="chat-input"
              className="flex-1 bg-muted/60 rounded-sm px-3 py-2 text-sm outline-none border border-border focus:border-primary" />
            <button onClick={send} disabled={busy || !input.trim()} className="bg-primary text-primary-foreground rounded-sm px-3 hover:opacity-90 disabled:opacity-50" data-testid="chat-send">
              <Send className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
